import React, { Component } from 'react'
import { IntlProvider, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import translations from '../translations/en.json'

const formatCompensation = (amount) => {
  if (!amount) return ''

  const numAmount = Number(amount)
  if (isNaN(numAmount)) return ''

  return `$${numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const formatCompensationShort = (amount) => {
  if (!amount) return ''

  const numAmount = Number(amount)
  if (isNaN(numAmount)) return ''

  if (numAmount >= 1000) {
    const thousands = numAmount / 1000
    if (thousands === Math.floor(thousands)) {
      return `$${thousands}k`
    }
    if (thousands === Math.floor(thousands * 10) / 10) {
      return `$${thousands.toFixed(1)}k`
    }
  }

  return `$${numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const DemoContainer = styled.div`
  padding: 40px;
  background: #f5f5f5;
  min-height: 100vh;
`

const DemoSection = styled.div`
  background: white;
  margin-bottom: 40px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const DemoTitle = styled.h2`
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
  font-size: 24px;
  color: #333;
`

const DemoDescription = styled.p`
  margin: 0 0 20px 0;
  color: #666;
  font-style: italic;
`

const CompensationTag = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 8px;
  gap: 8px;
  height: 28px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  margin-top: 10px;
  margin-bottom: 20px;
  background-color: ${(props) => props.backgroundColor || 'rgba(0, 0, 0, 0.12)'};
  color: ${(props) => props.color || '#666'};
  font-family: ${(props) => props.fontFamily || 'Arial, sans-serif'};
  width: fit-content;
`

const MoneyIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_money_icon)">
      <path
        d="M23.2935 4.44186C21.8389 3.83173 20.3831 3.59961 18.9281 3.59961C14.3093 3.59923 9.69075 5.93736 5.07188 5.93736C3.9135 5.93736 2.75588 5.79036 1.5975 5.42286C1.46738 5.38161 1.33688 5.36211 1.20938 5.36211C0.564 5.36211 0 5.86161 0 6.55498V18.4522C0 18.9259 0.271125 19.3747 0.7065 19.557C2.16113 20.1675 3.61688 20.3996 5.07188 20.3996C9.69075 20.3996 14.3096 18.0615 18.9285 18.0615C20.0869 18.0615 21.2445 18.2085 22.4029 18.576C22.533 18.6172 22.6635 18.6367 22.791 18.6367C23.4364 18.6367 24.0004 18.1372 24.0004 17.4439V5.54698C24 5.07298 23.7289 4.62448 23.2935 4.44186ZM1.8 7.35786C2.5545 7.54686 3.342 7.64173 4.152 7.69273C3.9315 8.79486 2.9625 9.62548 1.8 9.62548V7.35786ZM1.8 18.0454V16.2536C3.08888 16.2536 4.13175 17.2762 4.18913 18.5561C3.34425 18.4882 2.5545 18.3195 1.8 18.0454ZM12 15.5996C10.3429 15.5996 9 13.9875 9 11.9996C9 10.0114 10.3433 8.39961 12 8.39961C13.6568 8.39961 15 10.0114 15 11.9996C15 13.9882 13.6564 15.5996 12 15.5996ZM22.2 16.6414C21.543 16.4767 20.8609 16.3845 20.163 16.3249C20.3831 15.3469 21.1943 14.6044 22.2 14.4769V16.6414ZM22.2 7.78723C21.0416 7.64061 20.1428 6.67348 20.1071 5.47911C20.8399 5.56048 21.5355 5.71273 22.2 5.95386V7.78723Z"
        fill={color || 'rgba(0, 0, 0, 0.6)'}
      />
    </g>
    <defs>
      <clipPath id="clip0_money_icon">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const PositionInfoBox = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 4px;
  border-left: 3px solid #007bff;
`

const PositionInfoTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #333;
`

const PositionInfoField = styled.p`
  margin: 5px 0;
  font-size: 14px;
  color: #666;
`

class DemoCompensation extends Component {
  renderCompensationDemo(scenario) {
    const { title, description, compType, minComp, maxComp, pubComp } = scenario

    const shouldShowCompensation = pubComp === 1 && minComp && maxComp

    return (
      <DemoSection>
        <DemoTitle>{title}</DemoTitle>
        <DemoDescription>{description}</DemoDescription>

        {shouldShowCompensation && (
          <>
            <CompensationTag>
              <MoneyIcon />
              <span>
                {formatCompensationShort(minComp)} - {formatCompensationShort(maxComp)}{' '}
                <FormattedMessage
                  id={compType === 2 ? 'positionInfo.perYear' : 'positionInfo.perHour'}
                />
              </span>
            </CompensationTag>

            <PositionInfoBox>
              <PositionInfoTitle>Position Information</PositionInfoTitle>
              <PositionInfoField>Status: Full Time</PositionInfoField>
              <PositionInfoField>
                <FormattedMessage
                  id={compType === 2 ? 'positionInfo.baseSalaryRange' : 'positionInfo.payRange'}
                />
                : {formatCompensation(minComp)} - {formatCompensation(maxComp)}
                <FormattedMessage
                  id={compType === 2 ? 'positionInfo.yearSuffix' : 'positionInfo.hourSuffix'}
                />
              </PositionInfoField>
            </PositionInfoBox>
          </>
        )}

        {!shouldShowCompensation && (
          <PositionInfoBox>
            <PositionInfoTitle>Position Information</PositionInfoTitle>
            <PositionInfoField>Status: Full Time</PositionInfoField>
            <PositionInfoField style={{ fontStyle: 'italic', color: '#999' }}>
              (No compensation displayed - pubCompensation is disabled or values are missing)
            </PositionInfoField>
          </PositionInfoBox>
        )}
      </DemoSection>
    )
  }

  render() {
    const scenarios = [
      {
        title: 'Scenario 1: Salaried Position with Compensation',
        description:
          'Tests: Large salary range ($80K-$120K), K notation in tag, full amounts in Position Info',
        compType: 2,
        minComp: 80000,
        maxComp: 120000,
        pubComp: 1,
      },
      {
        title: 'Scenario 2: Hourly Position with Compensation',
        description: 'Tests: Hourly rate display ($25-$40/hr), per hour vs per year labels',
        compType: 1,
        minComp: 25,
        maxComp: 40,
        pubComp: 1,
      },
      {
        title: 'Scenario 3: Salaried with $30K-$60K Range',
        description: 'Tests: Even thousands formatting ($30k, $60k), exact match to Figma example',
        compType: 2,
        minComp: 30000,
        maxComp: 60000,
        pubComp: 1,
      },
      {
        title: 'Scenario 4: Hourly with $15-$25 Range',
        description: 'Tests: Lower hourly rates, different Pay Range label vs Base Salary Range',
        compType: 1,
        minComp: 15,
        maxComp: 25,
        pubComp: 1,
      },
      {
        title: 'Scenario 5: Precise Numbers (No Rounding)',
        description:
          'Tests: $35,550 and $67,890 should NOT be rounded, displayed with commas in full format',
        compType: 2,
        minComp: 35550,
        maxComp: 67890,
        pubComp: 1,
      },
      {
        title: 'Scenario 6: Half-K Values ($30.5K, $60.5K)',
        description: 'Tests: $30,500 → $30.5k formatting, single decimal place in K notation',
        compType: 2,
        minComp: 30500,
        maxComp: 60500,
        pubComp: 1,
      },
      {
        title: 'Scenario 7: No Compensation Data',
        description: 'Tests: Should NOT display compensation tag or field when values are null',
        compType: 2,
        minComp: null,
        maxComp: null,
        pubComp: 0,
      },
      {
        title: 'Scenario 8: pubCompensation Disabled',
        description:
          'Tests: Should NOT display compensation when pubCompensation=0 even though values exist',
        compType: 2,
        minComp: 90000,
        maxComp: 110000,
        pubComp: 0,
      },
    ]

    return (
      <IntlProvider locale="en" messages={translations}>
        <DemoContainer>
          <h1 style={{ marginBottom: '30px', fontSize: '32px' }}>Compensation Display Demo Page</h1>
          <p style={{ marginBottom: '40px', fontSize: '16px', color: '#666' }}>
            This page demonstrates various compensation display scenarios for visual verification.
            Use this to check styling, formatting, and behavior before testing with real data.
          </p>

          {scenarios.map((scenario, index) => (
            <React.Fragment key={index}>{this.renderCompensationDemo(scenario)}</React.Fragment>
          ))}
        </DemoContainer>
      </IntlProvider>
    )
  }
}

export default DemoCompensation
